import { useState } from 'react';
import { RefreshCw, Play, AlertCircle } from 'lucide-react';
import { cn } from '@/src/infrastructure/lib/utils';
import type { DeadLetterOutboxEvent } from '../types';
import { useRequeueOutbox } from '../hooks/useDeadLetterOutbox';

interface DeadLetterOutboxTableProps {
  events: DeadLetterOutboxEvent[];
  isLoading: boolean;
  onSuccess: (id: string) => void;
  onError?: (err: Error) => void;
}

export function DeadLetterOutboxTable({
  events,
  isLoading,
  onSuccess,
  onError,
}: DeadLetterOutboxTableProps) {
  const requeueMutation = useRequeueOutbox();
  const [requeueingId, setRequeueingId] = useState<string | null>(null);

  const handleRequeue = async (id: string) => {
    setRequeueingId(id);
    try {
      await requeueMutation.mutateAsync(id);
      onSuccess(id);
    } catch (err) {
      if (onError && err instanceof Error) onError(err);
    } finally {
      setRequeueingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="p-12 text-center text-xs text-on-surface-variant">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-primary" />
        Loading dead-lettered outbox events...
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border-subtle bg-surface-container/20 p-8 text-center">
        <div className="w-10 h-10 rounded-xl bg-surface-container flex items-center justify-center mx-auto mb-3 text-on-surface-variant">
          <AlertCircle className="w-5 h-5 text-emerald-400" />
        </div>
        <p className="text-sm font-semibold text-on-surface">No dead-lettered outbox events</p>
        <p className="text-xs text-on-surface-variant mt-1 max-w-sm mx-auto">
          All outbox events have succeeded or are within active retry sweeps.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border-subtle bg-surface-container/30 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="border-b border-border-subtle bg-surface-container/60 text-on-surface-variant font-medium">
              <th className="py-3 px-4">Event Type</th>
              <th className="py-3 px-4">Aggregate</th>
              <th className="py-3 px-4">Attempts</th>
              <th className="py-3 px-4">Last Error</th>
              <th className="py-3 px-4">Parked At</th>
              <th className="py-3 px-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle/40">
            {events.map((evt) => {
              const isRequeueing = requeueingId === evt.id;
              return (
                <tr key={evt.id} className="hover:bg-surface-container/40 transition-colors">
                  <td className="py-3 px-4">
                    <span className="font-mono font-semibold text-primary">{evt.eventType}</span>
                    <div className="text-[10px] text-on-surface-variant font-mono">{evt.id}</div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-medium text-on-surface">{evt.aggregateType}</span>
                    <div className="text-[10px] text-on-surface-variant font-mono">{evt.aggregateId}</div>
                  </td>
                  <td className="py-3 px-4 font-mono">{evt.attempts}</td>
                  <td className="py-3 px-4 max-w-xs truncate" title={evt.lastError ?? ''}>
                    <span className="text-rose-400 font-mono text-[11px]">
                      {evt.lastError ?? 'Unknown error'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-on-surface-variant text-[11px]">
                    {evt.deadLetteredAt ? new Date(evt.deadLetteredAt).toLocaleString() : '—'}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      type="button"
                      disabled={isRequeueing}
                      onClick={() => void handleRequeue(evt.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary hover:bg-primary-hover disabled:opacity-50 text-on-primary font-bold text-[11px] transition-all"
                    >
                      <Play className={cn('w-3 h-3', isRequeueing && 'animate-spin')} />
                      <span>{isRequeueing ? 'Requeueing…' : 'Requeue'}</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
